import { useEffect, useMemo, useReducer } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  ApiReportResponse,
  CancellationToken,
  PublicTabularResponse,
  QueryLoggingItem,
  ReportField,
  RunApiReportRequest,
  RunTabularReportRequest,
  SortField,
  TabularDataResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { getColumns, getConditionsHash } from "../../../../utilities/gridStateHelper.ts";
import { isTabularConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import { gridDataStateReducer, initialGridDataState } from "../storage/gridDataState.ts";
import type { TabularGridState } from "./gridState.types.ts";
import useDataLoadingViewer from "./useDataLoadingViewer.ts";

interface Props {
  fields: ReportField[];
  sorting: SortField[];
  requestConfig: RunTabularReportRequest;
  runReport: <
    TBody extends RunTabularReportRequest | RunApiReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  setResponse: ((response: PublicTabularResponse | undefined) => void) | undefined;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}
export default function useGridStateViewer({
  fields,
  sorting,
  setEtag,
  requestConfig,
  setResponse,
  updateDevTools,
  runReport,
}: Props): TabularGridState & { initialized: boolean } {
  const { dimensions, refreshToken, conditions } = useBiCommonDataContext();
  const [state, dispatch] = useReducer(gridDataStateReducer, initialGridDataState);
  const { loadData, cancelDataLoading, failedMeasures, handleInvalidConfiguration } = useDataLoadingViewer(
    requestConfig,
    dispatch,
    runReport,
    setResponse,
    updateDevTools,
    setEtag
  );

  const columns = useMemo(() => getColumns(fields, state.columns), [fields, state.columns]);

  const conditionsHash = useMemo(() => {
    return getConditionsHash(conditions, fields);
  }, [conditions, fields]);

  const configurationValidity = useMemo(() => {
    return isTabularConfigurationValid(conditions, fields, dimensions);
  }, [conditions, dimensions, fields]);

  useEffect(() => {
    if (configurationValidity.valid) {
      loadData();
    } else {
      handleInvalidConfiguration();
      cancelDataLoading();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsHash, refreshToken, configurationValidity.valid, sorting]);

  return {
    rows: state.rows,
    hasResult: state.rows !== initialGridDataState.rows,
    snapshot: state.snapshot,
    columns,
    groupTotals: state.groupTotals,
    grandTotals: state.grandTotals,
    totalCount: state.totalCount,
    loading: state.loading,
    error: state.error,
    configurationValidity,
    failedMeasures,
    initialized: state.initialized,
  };
}
